/**
 * What the run is for, and what the gate at the end of it does (2026-09-07).
 *
 * ## First: the gate said nothing, and did the wrong thing
 *
 * User: *"I completed the entire dungeon, reached the exit, held E, and the
 * red exit bar simply disappeared, but nothing happened. The game did not
 * end, which feels very strange."*
 *
 * It did not end because `run-lifecycle.md` Core Rule #3 made the **floor**
 * decide: a shallow gate always descended, and only the Vaults ever banked a
 * bag. The banner still said "extract" and "you lose everything you carried"
 * on all three floors, and the compass called every gate an `Exit`, so
 * somebody who did exactly what they were told had no way to learn otherwise.
 *
 * ## Then: the rule itself was backwards
 *
 * User: *"Decide how's better and do it."*
 *
 * Fixing the words would have left the real problem standing. The cheapest
 * way into the dungeon — a free Cellars run — was the **longest and riskiest
 * commitment in the game**: three floors, three Wardens, ninety rooms since
 * the route pass, and nothing kept unless you survived all of it. The most
 * expensive card, a Vaults Entry, was the *shortest and safest*: one floor,
 * one gate, banked. Money bought its owner out of the grind instead of into
 * the danger, and a new delver with no Wax could not bank a single coin until
 * they had beaten the hardest floor in the game.
 *
 * It also deleted the decision the genre is built on. game-concept.md §2 names
 * the tension as *"do I extract or push for one more chest"* — and on two
 * floors out of three there was no extract to weigh, only push.
 *
 * So the floor no longer decides. At any open gate:
 *
 *   **E** — climb out and bank everything you are carrying.
 *   **G** — descend, carrying it as the stake, run still open.
 *
 * That makes descending a *bet of the bag in your hands* rather than a
 * corridor you are marched down, and it makes a contract the same decision
 * paid for up front: it starts you deep without staking a shallow run to get
 * there. E is the reflex key and E is the safe one, deliberately.
 *
 * These functions are the whole of what the player is told about it, in one
 * place and pure, so "every floor offers a way out" is something a test holds
 * rather than something a reader checks by eye across a 2,000-line bootstrap.
 *
 * `depth` is `Run.stratum`'s convention throughout: 1 = Cellars, 3 = Vaults.
 */

/** True on the deepest floor, where there is nothing below to descend into. */
export function isFinalFloor(depth: number): boolean {
  return depth >= 3;
}

import { t, N_ } from '../i18n/i18n';
import { pressKey, holdKey } from './interact-prompt';

/**
 * The sentences that name the floor below, whole (2026-10-16, i18n): "descend
 * into {floor}" cannot be declined in a language with cases, so each floor
 * has its own key. Index 0 is a floor this build does not know by name.
 */
const OBJECTIVE_DESCEND: readonly [string, string, string] = [
  N_('Objective: follow the route to the Bone Warden’s vault — every gate is beyond it. Side rooms are optional loot. Then crank a gate open: press {climbKey} to climb out and keep what you have found, or {descendKey} to descend to the floor below carrying it as the stake. Die, and you lose all of it.'),
  N_('Objective: follow the route to the Bone Warden’s vault — every gate is beyond it. Side rooms are optional loot. Then crank a gate open: press {climbKey} to climb out and keep what you have found, or {descendKey} to descend into the Ossuary carrying it as the stake. Die, and you lose all of it.'),
  N_('Objective: follow the route to the Bone Warden’s vault — every gate is beyond it. Side rooms are optional loot. Then crank a gate open: press {climbKey} to climb out and keep what you have found, or {descendKey} to descend into the Vaults carrying it as the stake. Die, and you lose all of it.'),
];
const GATE_DESCEND: readonly [string, string, string] = [
  N_('{press} to descend to the floor below — your bag rides on it'),
  N_('{press} to descend into the Ossuary — your bag rides on it'),
  N_('{press} to descend into the Vaults — your bag rides on it'),
];

/** 1 → the Ossuary's sentence, 2 → the Vaults', anything else → "the floor below". */
function belowIndex(depth: number): 0 | 1 | 2 {
  return depth === 1 ? 1 : depth === 2 ? 2 : 0;
}

export function objectiveFor(depth: number): string {
  if (isFinalFloor(depth)) {
    return t('Objective: follow the route to the Bone Warden’s vault — every gate is beyond it. Side rooms are optional loot. Then crank a gate open and press {climbKey} to climb out with everything you are carrying. Die, and you lose all of it.', { climbKey: 'E' });
  }
  // Both options, named, in the order they should be considered: the safe one
  // first. A player reading this before they are standing at a gate is the
  // whole point — the decision is worth more if it is anticipated.
  return t(OBJECTIVE_DESCEND[belowIndex(depth)], { climbKey: 'E', descendKey: 'G' });
}

/**
 * The compass label.
 *
 * Just "Exit" again, on every floor, and correctly this time: a gate is a way
 * out everywhere now. It said "Exit" before the choice existed, when that was
 * a lie on two floors out of three.
 */
export function gateCompassLabel(): string {
  return t('Exit');
}

/**
 * The lines shown while standing at a gate — one per available action.
 *
 * "hold" for the crank and "press" for what comes after, because they are
 * genuinely different inputs, and conflating them is what produced the
 * original report: `handleInteractStart` fires on keydown, so a single
 * unbroken hold can only ever perform the first of the two.
 *
 * Returns an array so the caller can render one line per choice rather than
 * cramming a decision that stakes an entire run into a single sentence.
 */
export function gatePromptLines(depth: number, gateState: string): string[] {
  if (gateState !== 'OPEN') return [t('{hold} to crank the gate open', { hold: holdKey() })];
  const out = t('{press} to climb out and bank your loot', { press: pressKey() });
  return isFinalFloor(depth) ? [out] : [out, t(GATE_DESCEND[belowIndex(depth)], { press: pressKey('G') })];
}
